import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './GoogleMapsPicker.css';

const GoogleMapsPicker = ({
  value = {},
  onChange,
  placeholder = 'Search location...',
  disabled = false,
}) => {
  const [address, setAddress]       = useState(value.address || '');
  const [showModal, setShowModal]   = useState(false);
  const [loading, setLoading]       = useState(false);
  const [locating, setLocating]     = useState(false);
  const [error, setError]           = useState(null);
  const [mapReady, setMapReady]     = useState(false);
  const [selectedAddr, setSelectedAddr] = useState(value.address || '');

  const mapRef            = useRef(null);
  const markerRef         = useRef(null);
  const mapInstanceRef    = useRef(null);
  const autocompleteRef   = useRef(null);
  const searchInputRef    = useRef(null);

  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  /* ── Load Google Maps SDK ── */
  useEffect(() => {
    if (window.google?.maps) return;
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onerror = () => setError('Failed to load Google Maps. Check API key.');
    document.head.appendChild(script);
  }, [GOOGLE_MAPS_API_KEY]);

  /* ── Reverse geocode lat/lng → address ── */
  const reverseGeocode = useCallback(async (lat, lng) => {
    if (!window.google?.maps) return;
    setLoading(true);
    try {
      const geocoder = new window.google.maps.Geocoder();
      const result   = await geocoder.geocode({ location: { lat, lng } });
      if (result.results?.[0]) {
        const place      = result.results[0];
        const fullAddr   = place.formatted_address;
        const comps      = {};
        place.address_components?.forEach(c => {
          if (c.types.includes('route'))                          comps.street     = c.long_name;
          if (c.types.includes('street_number'))                  comps.buildingNo = c.long_name;
          if (c.types.includes('locality'))                       comps.city       = c.long_name;
          if (c.types.includes('administrative_area_level_1'))    comps.state      = c.short_name;
          if (c.types.includes('postal_code'))                    comps.pincode    = c.long_name;
        });
        setSelectedAddr(fullAddr);
        setError(null);
        onChange({ address: fullAddr, latitude: lat, longitude: lng, ...comps });
        placeMarker(lat, lng, fullAddr);
      }
    } catch (e) {
      setError('Could not get address for this location.');
    } finally {
      setLoading(false);
    }
  }, [onChange]);

  /* ── Place / move marker ── */
  const placeMarker = (lat, lng, title = '') => {
    if (!mapInstanceRef.current || !window.google?.maps) return;
    const pos = { lat, lng };
    mapInstanceRef.current.panTo(pos);
    mapInstanceRef.current.setZoom(16);
    if (markerRef.current) markerRef.current.setMap(null);
    markerRef.current = new window.google.maps.Marker({
      position: pos,
      map: mapInstanceRef.current,
      title,
      animation: window.google.maps.Animation.DROP,
    });
  };

  /* ── Init map inside modal ── */
  const initMap = useCallback(() => {
    if (!mapRef.current || !window.google?.maps) return;
    const defaultPos = (value.latitude && value.longitude)
      ? { lat: value.latitude, lng: value.longitude }
      : { lat: 28.6139, lng: 77.2090 };

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      zoom:               value.latitude ? 15 : 11,
      center:             defaultPos,
      disableDefaultUI:   false,
      zoomControl:        true,
      mapTypeControl:     false,
      streetViewControl:  false,
      fullscreenControl:  false,
      styles: [
        { featureType: 'poi',        elementType: 'labels', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit',    elementType: 'labels', stylers: [{ visibility: 'off' }] },
        { featureType: 'road',       elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
        { featureType: 'road',       elementType: 'labels.text.fill', stylers: [{ color: '#9ca3af' }] },
        { featureType: 'landscape',  elementType: 'geometry', stylers: [{ color: '#f3f4f6' }] },
        { featureType: 'water',      elementType: 'geometry', stylers: [{ color: '#bfdbfe' }] },
        { featureType: 'administrative', elementType: 'labels.text.fill', stylers: [{ color: '#374151' }] },
      ],
    });

    if (value.latitude && value.longitude) {
      placeMarker(value.latitude, value.longitude, selectedAddr);
    }

    mapInstanceRef.current.addListener('click', e => {
      reverseGeocode(e.latLng.lat(), e.latLng.lng());
    });

    setMapReady(true);
  }, [value.latitude, value.longitude, selectedAddr, reverseGeocode]);

  /* ── Init autocomplete on search input ── */
  const initAutocomplete = useCallback(() => {
    if (!searchInputRef.current || !window.google?.maps?.places) return;
    const ac = new window.google.maps.places.Autocomplete(searchInputRef.current, {
      types: ['geocode'],
      componentRestrictions: { country: 'in' },
    });
    autocompleteRef.current = ac;
    ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      if (!place.geometry) { setError('Select a valid location from the list.'); return; }
      const lat  = place.geometry.location.lat();
      const lng  = place.geometry.location.lng();
      const addr = place.formatted_address;
      const comps = {};
      place.address_components?.forEach(c => {
        if (c.types.includes('route'))                          comps.street     = c.long_name;
        if (c.types.includes('street_number'))                  comps.buildingNo = c.long_name;
        if (c.types.includes('locality'))                       comps.city       = c.long_name;
        if (c.types.includes('administrative_area_level_1'))    comps.state      = c.short_name;
        if (c.types.includes('postal_code'))                    comps.pincode    = c.long_name;
      });
      setSelectedAddr(addr);
      setError(null);
      onChange({ address: addr, latitude: lat, longitude: lng, ...comps });
      placeMarker(lat, lng, addr);
    });
  }, [onChange]);

  /* ── Open modal ── */
  const handleOpen = () => {
    setShowModal(true);
    setSelectedAddr(value.address || '');
    setError(null);
    setTimeout(() => {
      initMap();
      initAutocomplete();
    }, 120);
  };

  /* ── Use current location (GPS) ── */
  const handleCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) { setError('Geolocation is not supported by your browser.'); return; }
    setLocating(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setLocating(false);
        reverseGeocode(pos.coords.latitude, pos.coords.longitude);
      },
      err => {
        setLocating(false);
        if (err.code === 1) setError('Location access denied. Please allow location access.');
        else setError('Could not get your location. Try searching instead.');
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, [reverseGeocode]);

  const handleConfirm = () => {
    setAddress(selectedAddr);
    setShowModal(false);
  };

  const handleClose = () => {
    setShowModal(false);
    setError(null);
  };

  if (!GOOGLE_MAPS_API_KEY) {
    return <div className="gmp-error">⚠️ Google Maps API key not configured.</div>;
  }

  const hasCoords = !!(value.latitude && value.longitude);

  return (
    <>
      {/* ── Trigger input ── */}
      <div className={`gmp-trigger ${disabled ? 'gmp-trigger--disabled' : ''}`} onClick={!disabled ? handleOpen : undefined}>
        <span className="gmp-trigger-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
        </span>
        <span className={`gmp-trigger-text ${!address ? 'gmp-trigger-placeholder' : ''}`}>
          {address || placeholder}
        </span>
        {hasCoords && (
          <span className="gmp-trigger-coords">
            {value.latitude.toFixed(4)}, {value.longitude.toFixed(4)}
          </span>
        )}
        {!disabled && (
          <span className="gmp-trigger-cta">
            {hasCoords ? 'Change' : 'Pick'} →
          </span>
        )}
      </div>

      {/* ── Modal ── */}
      {showModal && createPortal(
        <div className="gmp-overlay" onClick={handleClose}>
          <div className="gmp-modal" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="gmp-modal-header">
              <div className="gmp-modal-header-left">
                <div className="gmp-modal-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                </div>
                <div>
                  <div className="gmp-modal-title">Select Location</div>
                  <div className="gmp-modal-subtitle">Search, click on map, or use current location</div>
                </div>
              </div>
              <button className="gmp-close-btn" onClick={handleClose} aria-label="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Search bar + Current Location */}
            <div className="gmp-search-row">
              <div className="gmp-search-wrap">
                <span className="gmp-search-icon">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  ref={searchInputRef}
                  id="gmp-search-input"
                  type="text"
                  placeholder="Search city, area, street…"
                  className="gmp-search-input"
                  autoComplete="off"
                />
              </div>
              <button
                className={`gmp-locate-btn ${locating ? 'gmp-locate-btn--loading' : ''}`}
                onClick={handleCurrentLocation}
                disabled={locating}
                title="Use my current location"
                type="button"
              >
                {locating ? (
                  <span className="gmp-spinner" />
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"/>
                    <line x1="12" y1="2" x2="12" y2="6"/>
                    <line x1="12" y1="18" x2="12" y2="22"/>
                    <line x1="2" y1="12" x2="6" y2="12"/>
                    <line x1="18" y1="12" x2="22" y2="12"/>
                  </svg>
                )}
                <span>{locating ? 'Locating…' : 'Use my location'}</span>
              </button>
            </div>

            {/* Error banner */}
            {error && (
              <div className="gmp-error-banner">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {error}
              </div>
            )}

            {/* Map */}
            <div className="gmp-map-wrap">
              <div ref={mapRef} className="gmp-map" />
              {!mapReady && (
                <div className="gmp-map-loading">
                  <span className="gmp-spinner gmp-spinner--lg" />
                  <span>Loading map…</span>
                </div>
              )}
              {loading && (
                <div className="gmp-map-overlay-msg">
                  <span className="gmp-spinner" />
                  <span>Getting address…</span>
                </div>
              )}
              <div className="gmp-map-hint">Tap anywhere on the map to drop a pin</div>
            </div>

            {/* Selected address preview */}
            <div className={`gmp-selected-addr ${selectedAddr ? 'gmp-selected-addr--active' : ''}`}>
              <div className="gmp-selected-addr-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
              </div>
              <div className="gmp-selected-addr-text">
                {selectedAddr || 'No location selected yet'}
              </div>
            </div>

            {/* Footer */}
            <div className="gmp-footer">
              <button className="gmp-btn gmp-btn--cancel" onClick={handleClose} type="button">
                Cancel
              </button>
              <button
                className="gmp-btn gmp-btn--confirm"
                onClick={handleConfirm}
                disabled={!selectedAddr}
                type="button"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Confirm Location
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default GoogleMapsPicker;